import React, { useEffect, useRef, useState } from 'react';
import Pill from '@/components/ui/Pill';
import Text from '@/components/ui/Text';
import { TCITooltip } from '@/components/ui/Tooltip';
import cn from '@/utils/cn';
import JsonBlock from '../LiveSessionsMonitor/JsonBlock';
import { catalogFor, toneFor, type Tone } from './eventCatalog';
import { groupEvents, slideRange, type EventGroup } from './groupEvents';
import { absoluteLabel, relativeLabel, skewSeconds } from './timeFormat';
import { actorKeyFor, type ActorKey, type LiveSessionEvent } from './types';

interface TimelineProps {
  events: LiveSessionEvent[];
  // An event cited by the AI summary: its row is outlined, its nav group expanded, and it is scrolled into view.
  highlightEventId?: number | null;
  startedAt: string;
  onSelectActor: (actor: ActorKey) => void;
}

const HIGHLIGHT_STYLE = { boxShadow: 'inset 4px 0 0 #522e91' };

const WINDOW = 300;

const ROW_CLASS = 'tw-grid tw-grid-cols-[88px_minmax(0,1fr)] tw-gap-3 tw-px-3 tw-py-2';
const TIME_CLASS = 'tw-font-mono tw-text-xs tw-text-very-dark-grey tw-whitespace-nowrap';
const LINK_BUTTON_CLASS = cn(
  'tw-bg-transparent tw-border-0 tw-p-0 tw-text-xs tw-text-tci-purple tw-cursor-pointer hover:tw-underline',
);
const PANEL_CLASS = 'tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg';

const TONE_ROW_CLASS: Record<Tone, string> = {
  bad: 'tw-bg-red-50',
  good: 'tw-bg-green-50',
  info: '',
  neutral: '',
  warn: 'tw-bg-yellow-50',
};

const TONE_DOT_CLASS: Record<Tone, string> = {
  bad: 'tw-bg-red-500',
  good: 'tw-bg-green-500',
  info: 'tw-bg-blue-400',
  neutral: 'tw-bg-gray-300',
  warn: 'tw-bg-yellow-500',
};

const ACTOR_PILL_COLOR = { Staffer: 'purple', Student: 'grey', system: 'blue' } as const;

const HIDDEN_METADATA_KEYS = ['emitter', 'process', 'seeded'];

interface ActorPillProps {
  event: LiveSessionEvent;
  onSelect: (actor: ActorKey) => void;
}

const ActorPill = ({ event, onSelect }: ActorPillProps) => {
  const label = event.actor ? (event.actor.name ?? `${event.actor.type} ${event.actor.id}`) : 'System';
  const color = event.actor ? ACTOR_PILL_COLOR[event.actor.type] : ACTOR_PILL_COLOR.system;
  return (
    <button
      aria-label={`Filter timeline to ${label}`}
      className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-shrink-0"
      type="button"
      onClick={() => onSelect(actorKeyFor(event))}
    >
      <Pill outline pillColor={color}>
        <Text className="tw-whitespace-nowrap" size="xs" tag="span">{label}</Text>
      </Pill>
    </button>
  );
};

const TimeCell = ({ event, startedAt }: { event: LiveSessionEvent; startedAt: string }) => {
  const skew = skewSeconds(event);
  return (
    <TCITooltip>
      <TCITooltip.Trigger asChild>
        <span className={cn(TIME_CLASS, 'tw-cursor-help')}>{relativeLabel(event.occurred_at, startedAt)}</span>
      </TCITooltip.Trigger>
      <TCITooltip.Content>
        <div className="tw-text-xs tw-flex tw-flex-col tw-gap-0.5">
          <span>{absoluteLabel(event.occurred_at)} server-normalized</span>
          <span>received {absoluteLabel(event.received_at)}</span>
          {skew !== null && Math.abs(skew) >= 2 && <span>client clock {skew > 0 ? '+' : ''}{skew}s</span>}
          {event.client_seq !== null && <span>tab seq #{event.client_seq}</span>}
        </div>
      </TCITooltip.Content>
    </TCITooltip>
  );
};

const MetadataDetails = ({ metadata }: { metadata: Record<string, unknown> }) => {
  const shown = Object.fromEntries(
    Object.entries(metadata).filter(([key]) => !HIDDEN_METADATA_KEYS.includes(key)),
  );
  if (Object.keys(shown).length === 0) return null;
  return (
    <details className="tw-mt-1">
      <summary className="tw-cursor-pointer tw-text-xs tw-text-very-dark-grey">metadata</summary>
      <JsonBlock className="tw-mt-1" value={shown} />
    </details>
  );
};

interface RowProps {
  event: LiveSessionEvent;
  highlighted?: boolean;
  startedAt: string;
  onSelectActor: (actor: ActorKey) => void;
}

const EventRow = ({ event, highlighted = false, startedAt, onSelectActor }: RowProps) => {
  const entry = catalogFor(event.event_type);
  const tone = toneFor(event.event_type, event.metadata);
  return (
    <li
      className={cn(ROW_CLASS, TONE_ROW_CLASS[tone])}
      data-highlighted={highlighted || undefined}
      id={`event-${event.id}`}
      style={highlighted ? HIGHLIGHT_STYLE : undefined}
    >
      <TimeCell event={event} startedAt={startedAt} />
      <div className="tw-min-w-0">
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
          <span aria-hidden className={cn('tw-h-2 tw-w-2 tw-rounded-full tw-shrink-0', TONE_DOT_CLASS[tone])} />
          <ActorPill event={event} onSelect={onSelectActor} />
          <Text className="tw-text-tci-black tw-m-0" size="sm" tag="span" weight="bold">{entry.label}</Text>
          <Text className="tw-text-very-dark-grey tw-m-0 tw-truncate" size="sm" tag="span">
            {entry.summarize(event.metadata)}
          </Text>
          <span className="tw-ml-auto tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-400">
            {event.source}
          </span>
        </div>
        <MetadataDetails metadata={event.metadata} />
      </div>
    </li>
  );
};

interface NavGroupRowProps {
  group: EventGroup;
  highlightEventId: number | null;
  startedAt: string;
  onSelectActor: (actor: ActorKey) => void;
}

const NavGroupRow = ({ group, highlightEventId, startedAt, onSelectActor }: NavGroupRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const first = group.events[0];
  const last = group.events[group.events.length - 1];
  const entry = catalogFor(first.event_type);
  const containsHighlight = highlightEventId !== null && group.events.some(e => e.id === highlightEventId);

  useEffect(() => {
    if (containsHighlight) setExpanded(true);
  }, [containsHighlight]);

  if (expanded) {
    return (
      <>
        {group.events.map(event => (
          <EventRow
            key={event.id}
            event={event}
            highlighted={event.id === highlightEventId}
            startedAt={startedAt}
            onSelectActor={onSelectActor}
          />
        ))}
        <li className="tw-px-3 tw-py-1">
          <button className={LINK_BUTTON_CLASS} type="button" onClick={() => setExpanded(false)}>
            Collapse {group.events.length} navs
          </button>
        </li>
      </>
    );
  }

  return (
    <li className={ROW_CLASS}>
      <span className={TIME_CLASS}>{relativeLabel(first.occurred_at, startedAt)}</span>
      <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
        <span aria-hidden className="tw-h-2 tw-w-2 tw-rounded-full tw-shrink-0 tw-bg-gray-300" />
        <ActorPill event={first} onSelect={onSelectActor} />
        <Text className="tw-text-tci-black tw-m-0" size="sm" tag="span" weight="bold">
          {group.events.length} × {entry.label}
        </Text>
        <Text className="tw-text-very-dark-grey tw-m-0" size="sm" tag="span">
          {slideRange(group.events)} · until {relativeLabel(last.occurred_at, startedAt)}
        </Text>
        <button className={cn(LINK_BUTTON_CLASS, 'tw-ml-auto')} type="button" onClick={() => setExpanded(true)}>
          Expand
        </button>
      </div>
    </li>
  );
};

const Timeline = ({ events, highlightEventId = null, startedAt, onSelectActor }: TimelineProps) => {
  const [limit, setLimit] = useState(WINDOW);
  const groups = groupEvents(events);
  const visible = groups.slice(0, limit);
  const scrolledToRef = useRef<number | null>(null);

  // Once per highlighted id: widen the window if the row is past it, then scroll to the row after
  // its group has had a render to expand. Guarded so the 5s live tail does not re-scroll.
  useEffect(() => {
    if (highlightEventId === null || scrolledToRef.current === highlightEventId) return undefined;
    const index = groups.findIndex(group => group.events.some(e => e.id === highlightEventId));
    if (index === -1) return undefined;
    if (index >= limit) {
      setLimit(index + WINDOW);
      return undefined;
    }
    scrolledToRef.current = highlightEventId;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(`event-${highlightEventId}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [groups, highlightEventId, limit]);

  if (events.length === 0) {
    return (
      <div className={cn(PANEL_CLASS, 'tw-p-12 tw-text-center')}>
        <Text className="tw-text-very-dark-grey tw-m-0" size="sm">No events match these filters.</Text>
      </div>
    );
  }

  return (
    <div className={cn(PANEL_CLASS, 'tw-overflow-hidden')}>
      <ol aria-label="Session timeline" className="tw-list-none tw-m-0 tw-p-0 tw-divide-y tw-divide-gray-100">
        {visible.map(group => (group.kind === 'navs'
          ? (
            <NavGroupRow
              key={group.key}
              group={group}
              highlightEventId={highlightEventId}
              startedAt={startedAt}
              onSelectActor={onSelectActor}
            />
          )
          : (
            <EventRow
              key={group.key}
              event={group.events[0]}
              highlighted={group.events[0].id === highlightEventId}
              startedAt={startedAt}
              onSelectActor={onSelectActor}
            />
          )
        ))}
      </ol>
      {groups.length > limit && (
        <div className="tw-p-3 tw-border-t tw-border-solid tw-border-gray-100 tw-text-center">
          <button
            className={cn(LINK_BUTTON_CLASS, 'tw-text-sm')}
            type="button"
            onClick={() => setLimit(limit + WINDOW)}
          >
            Show {Math.min(WINDOW, groups.length - limit)} more
          </button>
        </div>
      )}
    </div>
  );
};

export default Timeline;
